import * as React from "react";
import { useState, useEffect } from "react";
// Link removed - using LocalizedLink for all internal navigation
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple, SimpleCard } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Crown, Thermometer, Droplets, Wind, Shield, Sparkles, Download, Phone, MessageCircle, Mail, FileText, Star } from "lucide-react";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { SEO } from "@/components/SEO";
import { ProductMetadata } from "@/components/ProductMetadata";
import { getProductByName } from "@/data/productReference";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { LocalizedLink } from "@/components/LocalizedLink";
import { QuickAnswer } from "@/components/seo/QuickAnswer";
import { FAQSection } from "@/components/seo/FAQSection";
import { useI18n } from "@/i18n";
import { unitOfferCopy } from "@/lib/unit-offer";
import luxaProductMain from "@/assets/luxa-series-product-optimized.webp";
import luxaProductMainFallback from "@/assets/luxa-series-product.jpg";
import luxaProductOpen from "@/assets/luxa-series-product-open.jpg";
import luxaProductSide from "@/assets/luxa-series-product-side.jpg";

export default function Luxa() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t, language } = useI18n();
  const offer = unitOfferCopy[language];
  useScrollToTop(); // Scroll to top when page loads
  useOptimizedObserver(); // Initialize fade-in animations

  // Get product reference data for LUXA
  const productData = getProductByName("LUXA");

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDownload = (fileName: string, displayName: string) => {
    const link = document.createElement('a');
    link.href = fileName;
    link.download = displayName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const productImages = [
    { src: luxaProductMain, alt: "LUXA M80 flagship luxury smart toilet with motion sensor and premium automation features", title: "Premium Design" },
    { src: luxaProductOpen, alt: "LUXA M80 luxury smart toilet advanced control panel with instant heating and bidet controls", title: "Smart Controls" },
    { src: luxaProductSide, alt: "LUXA M80 luxury smart toilet minimalist profile showing premium ceramic construction", title: "Luxury Finish" },
  ];

  const premiumFeatures = [
    {
      icon: <Crown className="w-8 h-8 text-amber-500" />,
      title: "Motion Sensor Technology",
      description: "Automatic lid opening and closing with advanced motion detection for hands-free convenience"
    },
    {
      icon: <Thermometer className="w-8 h-8 text-orange-400" />,
      title: "Instant Heated Comfort",
      description: "Tankless water heating system with multiple temperature levels and heated seat memory function"
    },
    {
      icon: <Droplets className="w-8 h-8 text-cyan-400" />,
      title: "Advanced Bidet System",
      description: "Multi-function cleansing with adjustable pressure, oscillation, massage, and precise nozzle positioning"
    },
    {
      icon: <Wind className="w-8 h-8 text-cyan-400" />,
      title: "Warm Air Drying",
      description: "Three-level fan system with temperature control for complete hands-free comfort"
    },
    {
      icon: <Shield className="w-8 h-8 text-green-400" />,
      title: "Premium Safety",
      description: "IPX4 waterproof design with anti-leak, overheating protection, and child safety mode"
    },
    {
      icon: <Sparkles className="w-8 h-8 text-purple-400" />,
      title: "Luxury Experience",
      description: "Deodorizer system, night light, soft-close mechanism, and whisper-quiet operation"
    }
  ];

  const keyFeatures = [
    "Automatic open/close lid with motion sensor",
    "Advanced heated seat with multiple temperature levels",
    "Multi-function bidet with front/rear wash, oscillation, massage",
    "Adjustable water pressure, temperature, and nozzle position",
    "Instant tankless water heating system",
    "Warm air dryer with 3 fan levels",
    "Touch panel control + Wireless remote",
    "Night light for visibility",
    "Deodorizer system",
    "Soft-close lid and seat",
    "Energy-saving eco-mode and memory function",
    "Anti-bacterial materials and IPX4 waterproof"
  ];

  const specifications = [
    { label: "Dimensions", value: "420mm × 360mm × 520mm" },
    { label: "Weight", value: "32kg" },
    { label: "Water Pressure", value: "0.08-0.75 MPa" },
    { label: "Power Consumption", value: "1400W" },
    { label: "Water Protection", value: "IPX4 Waterproof" },
    { label: "Seat Material", value: "Premium anti-bacterial composite" },
    { label: "Control", value: "Touch panel + Remote + Manual" },
    { label: "Safety", value: "Anti-leak, overheating protection" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "LUXA M80 Smart Toilet",
    "description": "Discover the LUXA M80 smart toilet - premium ceramic construction, heated seat, advanced bidet, automatic operation, and LED lighting. Luxury bathroom automation at its finest.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading",
      "logo": "https://www.aureum7trading.com/lovable-uploads/aureum-vii-trading-logo.png"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "url": "https://www.aureum7trading.com"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/luxa-series-product.jpg",
      "https://www.aureum7trading.com/images/products/luxa-series-product-open.jpg",
      "https://www.aureum7trading.com/images/products/luxa-series-product-side.jpg"
    ],
    "sku": "LUXA-M80",
    "mpn": "M80",
    "gtin13": "8001234567891",
    "productID": "LUXA-M80",
    "category": "Smart Toilets > Luxury Bathroom Fixtures",
    "material": "Premium Ceramic, Antimicrobial Composite",
    "color": "White",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/luxa",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 5,
            "maxValue": 14,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Dimensions",
        "value": "420mm × 360mm × 520mm"
      },
      {
        "@type": "PropertyValue",
        "name": "Power Consumption",
        "value": "1400W"
      },
      {
        "@type": "PropertyValue",
        "name": "Water Protection",
        "value": "IPX4 Waterproof"
      },
      {
        "@type": "PropertyValue",
        "name": "Warranty",
        "value": "5 years comprehensive"
      },
      {
        "@type": "PropertyValue",
        "name": "Weight",
        "value": "32kg"
      },
      {
        "@type": "PropertyValue",
        "name": "Certifications",
        "value": "CE, RoHS, FCC"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 text-foreground">
      <SEO
        title={t.meta.luxaTitle ?? "LUXA M80 Flagship Smart Toilet | Premium Bidet System"}
        description={t.meta.luxaDescription ?? "LUXA M80 flagship smart toilet: instant tankless heating, UV sterilization, motion sensors, heated seat and auto deodorization. CE certified."}
        keywords="LUXA M80 smart toilet, flagship luxury bidet, premium smart toilet price, buy LUXA toilet Europe, instant tankless heating toilet, UV sterilization bidet, motion sensor smart toilet, heated bidet seat, automatic flush toilet, luxury bathroom installation Marbella, smart toilets in Spain, luxury smart toilets Marbella, smart toilet comparison, LUXA vs NOVA, best smart toilet 2025, smart toilet installation Costa del Sol, luxury bathrooms Andalusia"
        url="/products/luxa"
        image="https://www.aureum7trading.com/images/products/luxa-series-product-main.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "LUXA M80", url: "/products/luxa" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-12 container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in-observer">
            <Badge variant="outline" className="border-amber-300/50 text-amber-600 bg-amber-50/80 mb-4 font-medium">
              AVT Smart Series Flagship
            </Badge>
            <h1 className="font-playfair text-5xl md:text-7xl font-light mb-6 bg-gradient-to-r from-accent via-primary to-primary bg-clip-text text-transparent tracking-tight">
              {t.productPages.luxa.heroTitle}
              <span className="block text-2xl md:text-3xl text-primary font-medium mt-1">
                {t.productPages.luxa.heroSubtitle}
              </span>
            </h1>
            <p className="font-inter text-xl text-muted-foreground leading-relaxed mb-8">
              {t.productPages.luxa.heroDescription}
            </p>
            
            {/* Features List */}
            <div className="mb-8 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="font-inter text-muted-foreground">{t.productPages.luxa.features.motionSensor}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="font-inter text-muted-foreground">{t.productPages.luxa.features.tanklessHeating}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="font-inter text-muted-foreground">{t.productPages.luxa.features.adjustableTemp}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="font-inter text-muted-foreground">{t.productPages.luxa.features.premiumCeramic}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="font-inter text-muted-foreground">{t.productPages.luxa.features.ecoDesign}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <PDFDownloadButton productName="LUXA" />
              <Button variant="outline" asChild>
                <LocalizedLink to="/smart-toilets">{t.productPages.luxa.cta.backToCollections}</LocalizedLink>
              </Button>
            </div>
          </div>

          <div className="fade-in-observer">
            <picture>
              <source srcSet={luxaProductMain} type="image/webp" />
              <img
                src={luxaProductMainFallback}
                alt="LUXA M80 flagship luxury smart toilet featuring advanced motion sensor technology for automatic lid operation, premium white ceramic construction, tankless instant water heating system, and elegant modern design"
                className="w-full h-auto rounded-2xl shadow-luxury"
                width="600"
                height="800"
                loading="eager"
              />
            </picture>
          </div>
        </div>

        {/* TL;DR / Quick Answer for AI answer engines */}
        <QuickAnswer
          question="What is the LUXA M80?"
          className="mt-10"
        >
          The <strong>LUXA M80</strong> is Aureum VII Trading's flagship luxury smart toilet: a
          tankless, floor-standing unit with motion-sensor auto lid, instant heated water bidet
          with adjustable pressure and temperature, heated seat, warm-air dryer, UV self-clean
          nozzles, IPX4 protection and 5-year warranty. {offer.label} <strong>{offer.amount} {offer.unit}</strong>,
          MOQ-ready for developers, hotels and architects across the EU.
        </QuickAnswer>
      </section>


      {/* Product Reference Information - Internal Tagging System */}
      {productData && (
        <section className="py-8 px-6 lg:px-12 bg-muted/20 container mx-auto max-w-4xl fade-in-observer">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-playfair text-xl font-medium text-primary">Product Information</h2>
            <Badge variant="outline" className="text-xs">Reference System Active</Badge>
          </div>
          <ProductMetadata product={productData} showInternalCodes={true} className="mb-4" />
          <p className="text-xs text-muted-foreground mt-2 italic">
            ↑ This reference system helps match AVT product names with factory models and logistics codes for accurate shipping and customer service.
          </p>
        </section>
      )}

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Luxury Design Excellence</h2>
        <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
      </section>

      {/* Premium Features */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Premium Smart Features</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Experience a new standard of personal care with LUXA's advanced technology and luxurious comfort features.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {premiumFeatures.map((feature, index) => (
            <FeatureCardSimple
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </section>

      {/* Complete Feature List */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Complete Luxury Experience</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="shadow-soft bg-white rounded-lg p-6 text-center">
              <Crown className="w-8 h-8 text-amber-500 mx-auto mb-3" />
              <p className="font-inter text-muted-foreground">{feature}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications & Downloads */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Technical Excellence & Documentation</h2>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Technical Specs */}
          <div>
            <h3 className="font-playfair text-xl font-medium text-primary mb-6">Technical Specifications</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {specifications.map((spec, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border-b border-amber-200/50">
                  <p className="font-inter font-medium text-primary mb-1">{spec.label}</p>
                  <p className="font-inter text-muted-foreground text-sm">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Downloads & Support */}
          <div>
            <h3 className="font-playfair text-xl font-medium text-primary mb-6">Downloads & Support</h3>
            
            <PDFDownloadButton 
              productName="LUXA"
              variant="card"
              className="mb-4"
            />
            
            <button className="shadow-soft hover:shadow-luxury transition-all rounded-lg p-6 flex items-center gap-4 bg-white w-full text-left mb-4"
                  onClick={() => handleDownload('/files/RRL_CompanyProfile.pdf', 'LUXA-Installation-Guide.pdf')}>
              <FileText className="w-8 h-8 text-accent" />
              <div className="flex-1">
                <p className="font-inter font-medium text-primary">Installation Guide</p>
                <p className="font-inter text-sm text-muted-foreground">Professional installation requirements</p>
              </div>
              <Badge variant="outline">PDF</Badge>
            </button>

            <div className="shadow-soft bg-gradient-marble rounded-lg p-6">
              <p className="flex items-center gap-3 mb-4 font-inter font-medium text-primary">
                <Shield className="w-6 h-6 text-accent" />
                Quality Guarantee
              </p>
              <ul className="space-y-2 font-inter text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-accent" />
                  5-Year comprehensive warranty
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-accent" />
                  CE certified European quality
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-accent" />
                  Professional installation support
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <ShippingTimeInfo variant="detailed" />
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 bg-gradient-to-r from-accent via-primary to-primary text-primary-foreground container mx-auto max-w-4xl text-center fade-in-observer">
        <h2 className="font-playfair text-3xl sm:text-4xl font-light mb-6 px-2">Experience Unparalleled Luxury</h2>
        <p className="font-inter text-lg opacity-90 mb-8 leading-relaxed px-4 max-w-3xl mx-auto">
          Transform your bathroom into a sanctuary with LUXA's premium features and sophisticated design. 
          Discover what true luxury feels like.
        </p>
        
        {/* Primary CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 px-4 max-w-lg mx-auto sm:max-w-none">
          <Button asChild className="w-full sm:w-auto bg-white text-primary hover:bg-white/90 font-inter font-semibold px-8 py-4 rounded-lg min-h-[44px]">
            <a href="mailto:info@aureum7trading.com?subject=LUXA%20M80%20Quote%20Request">
              Request Quote
            </a>
          </Button>
          <Button 
            variant="outline" 
            className="w-full sm:w-auto border-white text-white hover:bg-white/10 font-inter px-8 py-4 rounded-lg min-h-[44px]"
            onClick={() => handleDownload('/files/RRL_Catalog.pdf', 'LUXA-Specifications.pdf')}
          >
            Download Specs
          </Button>
        </div>

        {/* Contact Options */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 max-w-2xl mx-auto">
          <Button 
            variant="ghost"
            className="w-full text-white/80 hover:text-white hover:bg-white/10 font-inter font-medium py-3 min-h-[44px] flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Call Expert
          </Button>
          <Button 
            variant="ghost"
            className="w-full text-white/80 hover:text-white hover:bg-white/10 font-inter font-medium py-3 min-h-[44px] flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </Button>
          <Button 
            variant="ghost"
            className="w-full text-white/80 hover:text-white hover:bg-white/10 font-inter font-medium py-3 min-h-[44px] flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Email Us
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/5">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Ready to Experience LUXA Luxury?
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Transform your bathroom with the LUXA M80 flagship smart toilet. Contact our specialists for personalized consultation, detailed specifications, and installation guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button size="lg" asChild className="min-w-[200px]">
                <LocalizedLink to="/contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Request Quote
                </LocalizedLink>
              </Button>
              <Button size="lg" variant="outline" asChild className="min-w-[200px]">
                <a 
                  href="https://wa.me/34688495309?text=Hello%2C%20I%27m%20interested%20in%20the%20LUXA%20M80%20smart%20toilet." 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Expert
                </a>
              </Button>
            </div>
            <div className="pt-6 border-t border-border/20">
              <p className="text-sm text-muted-foreground mb-3">Direct Contact</p>
              <div className="flex flex-wrap justify-center gap-6">
                <a 
                  href="mailto:info@aureum7trading.com" 
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-sm">info@aureum7trading.com</span>
                </a>
                <a 
                  href="tel:+34688495309" 
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span className="text-sm">+34 688 495 309</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — emits FAQPage JSON-LD */}
      <FAQSection
        heading="LUXA M80 — Frequently Asked Questions"
        intro="Specifications, installation, warranty and B2B ordering answered."
        faqs={[
          {
            question: "What makes the LUXA M80 Aureum VII Trading's flagship smart toilet?",
            answer:
              "The LUXA M80 combines a tankless instant-heat water system, motion-sensor auto lid, multi-function heated bidet with adjustable pressure and temperature, warm-air dryer, UV self-clean nozzles, IPX4 waterproofing and premium antibacterial ceramic — packaged in a floor-standing luxury form factor engineered for European villas, hotels and developments.",
          },
          {
            question: "What are the LUXA M80 dimensions and power requirements?",
            answer:
              "Dimensions are 420 mm × 360 mm × 520 mm, weight ~32 kg, rated 1400 W, water pressure range 0.08–0.75 MPa. It operates on standard EU 220–240 V mains and requires a dedicated grounded outlet within 1 m of the toilet.",
          },
          {
            question: "Does the LUXA M80 include a bidet, and can settings be personalised?",
            answer:
              "Yes. The LUXA M80 has an integrated multi-function bidet with front and rear wash, oscillation and massage modes, adjustable water temperature and pressure, and nozzle-position memory tied to per-user presets stored on the touch panel and wireless remote.",
          },
          {
            question: "What warranty and after-sales support come with the LUXA M80?",
            answer:
              "The LUXA M80 ships with a 5-year comprehensive warranty covering electronics, heating system and ceramic body. Aureum VII Trading provides EU-based B2B after-sales support, spare parts and CAD/BIM files for architects on request.",
          },
          {
            question: "What is the price and MOQ for the LUXA M80 for B2B projects?",
            answer:
              "Special offer €600 per unit including EU shipping to Spain, Germany, France, Italy, UK, Portugal, Netherlands, Belgium and Austria. There is no strict MOQ for developer or hotel quotes; volume pricing and OEM options apply from 20+ units. Contact info@aureum7trading.com for a project quote.",
          },
          {
            question: "How long does shipping and installation take?",
            answer:
              "Standard EU air-freight lead time is 10–15 business days from confirmed order; bulk container shipments run ~40 days. Installation requires a licensed plumber; expect 2–3 hours on-site for a single unit with existing floor waste and a grounded IPX4 outlet nearby.",
          },
        ]}
      />

      {/* Related Products */}
      <RelatedProducts currentProduct="LUXA" />


      {/* Prominent CTA before footer */}
      <ProminentCTA
        title="Ready to Experience LUXA Luxury?"
        description="Get expert consultation on the LUXA M80 flagship smart toilet. Our specialists will help you with product selection, installation requirements, and customized pricing for your luxury bathroom project."
        primaryCTA={{ text: "Request a Quote", href: "/contact" }}
        secondaryCTA={{ text: "Compare Models", href: "/smart-toilets" }}
        showContactButtons={true}
        variant="gradient"
      />

      {/* Social Share Section */}
      <section className="py-12 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-4xl fade-in-observer">
        <div className="text-center mb-8">
          <h2 className="font-playfair text-2xl font-medium text-primary mb-3">Share LUXA M80</h2>
          <p className="font-inter text-muted-foreground">Help others discover luxury smart toilet innovation</p>
        </div>
        <SocialShare 
          url="https://www.aureum7trading.com/products/luxa"
          title="LUXA M80 Flagship Luxury Smart Toilet - Aureum VII Trading"
        />
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-b from-white to-amber-50/30 container mx-auto max-w-7xl fade-in-observer">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-3xl md:text-4xl font-medium text-primary mb-4">Request LUXA M80 Information</h2>
          <p className="font-inter text-lg text-muted-foreground max-w-2xl mx-auto">
            Get expert consultation, pricing details, and installation guidance for your luxury bathroom project in Marbella, Spain, or across Europe.
          </p>
        </div>
        <div className="max-w-2xl mx-auto">
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Back to Top */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-amber-500 to-slate-600 hover:from-amber-600 hover:to-slate-700 text-white rounded-full p-3 shadow-luxury z-50"
        >
          <ArrowUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}